import { useAircraft } from '@/features/Aircrafts/api';
import { useCollection } from '@/features/AssetCollection/api';
import { resolveTargetSerial } from '@/features/AssetDetailView/FolderDetailView/H001HardTimeComponents/utils';
import {
    useComponentById,
    useComponentByPartNumber,
    useComponentItems,
    useProject,
} from '@/features/end-of-lease/api';
import {
    AircraftSchema,
    Component,
    ComponentItem,
    LeasingAircraft,
} from '@/features/end-of-lease/types';
import { AffectedObject } from '@/features/Feedback/types';

import { AffectedTarget } from './affectedTarget';
import { AFFECTED_OBJECT_VALUE_MAX_LENGTH } from './feedbackLimits';

type AircraftLabels = Pick<AircraftSchema, 'registration' | 'msn'>;

/** The slice of each page hook's data the label is read from. */
export interface LoadedLabels {
    aircraft?: AircraftLabels;
    project?: {
        msn: string | null;
        aircraft: AircraftLabels | null;
        leasingAircraft: Pick<LeasingAircraft, 'registration' | 'msn'>;
    } | null;
    collection?: { name: string };
    component?: Pick<Component, 'partNumber'>;
}

type MaybeText = string | null | undefined;

/** The first value that still has text after trimming, trimmed. */
function firstText(...values: MaybeText[]): string | null {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed) return trimmed;
    }
    return null;
}

function labelled(kind: AffectedObject['kind'], value: string | null): AffectedObject | null {
    return value === null ? null : { kind, value };
}

function describeComponent(serialNumber: string | null, partNumber: MaybeText): AffectedObject | null {
    const part = firstText(partNumber);
    if (part === null) return null;
    const serial = firstText(serialNumber);
    return { kind: 'htc_number', value: serial === null ? `P/N ${part}` : `P/N ${part} · S/N ${serial}` };
}

function describeProject(project: LoadedLabels['project']): AffectedObject | null {
    if (!project) return null;
    return labelled(
        'aircraft',
        firstText(
            project.aircraft?.registration,
            project.leasingAircraft.registration,
            project.aircraft?.msn,
            project.leasingAircraft.msn,
            project.msn,
        ),
    );
}

/** The value is shown read-only, so one over the endpoint's limit is cut instead of failing the send. The limit counts code points, so a character is never split. */
export function describeAffectedObject(target: AffectedTarget, loaded: LoadedLabels): AffectedObject | null {
    const described = describeTarget(target, loaded);
    return described && { ...described, value: Array.from(described.value).slice(0, AFFECTED_OBJECT_VALUE_MAX_LENGTH).join('') };
}

function describeTarget(target: AffectedTarget, loaded: LoadedLabels): AffectedObject | null {
    switch (target.kind) {
        case 'ad':
            return { kind: 'ad_number', value: target.adNumber };
        case 'componentPartNumber':
            return describeComponent(target.serialNumber ?? null, target.partNumber);
        case 'component':
            return describeComponent(target.serialNumber, loaded.component?.partNumber);
        case 'aircraft':
            return labelled('aircraft', firstText(loaded.aircraft?.registration, loaded.aircraft?.msn));
        case 'project':
            return describeProject(loaded.project);
        case 'collection':
            return labelled('collection', firstText(loaded.collection?.name));
        case 'none':
            return null;
    }
}

/**
 * A component link without a serial number gets the installed item's, the same
 * fallback the detail views use, so the label names the instance on screen.
 */
function withInstalledSerial(
    target: AffectedTarget,
    items: Pick<ComponentItem, 'status' | 'serialNumber'>[] | undefined,
): AffectedTarget {
    if (target.kind !== 'component' && target.kind !== 'componentPartNumber') return target;
    const installed = items?.find((item) => item.status === 'installed');
    return { ...target, serialNumber: resolveTargetSerial(target.serialNumber ?? undefined, installed) };
}

/**
 * Reads the affected object's label through the page's own hooks: cache first,
 * a fetch only when the target needs one. Each hook gets an id only for its own
 * target, so the others stay disabled.
 */
export function useAffectedObject(target: AffectedTarget): AffectedObject | null {
    const aircraft = useAircraft(target.kind === 'aircraft' ? target.aircraftId : 0);
    const project = useProject(target.kind === 'project' ? target.projectId : null);
    const collection = useCollection(target.kind === 'collection' ? target.collectionId : 0);
    const component = useComponentById(target.kind === 'component' ? target.componentId : undefined);
    const byPartNumber = useComponentByPartNumber(
        {
            projectId: target.kind === 'componentPartNumber' ? target.projectId : 0,
            partNumber: target.kind === 'componentPartNumber' ? target.partNumber : '',
        },
        { enabled: target.kind === 'componentPartNumber' },
    );
    const componentId = target.kind === 'component' ? target.componentId : byPartNumber.data?.id;
    const componentItems = useComponentItems(
        target.kind === 'component' || target.kind === 'componentPartNumber' ? componentId : undefined,
    );
    return describeAffectedObject(withInstalledSerial(target, componentItems.data), {
        aircraft: aircraft.data,
        project: project.data,
        collection: collection.data,
        component: component.data,
    });
}
